import { ImportHistoryApi } from '../../../api/importHistoryApi';
import { downloadFile, getFileNameFromHeader } from '../../../api/reportSettingsApi';
import { BatchDeterminationContext } from '../../../contexts/BatchDeterminationContext';
import { FileContext } from '../../../contexts/FileContext';
import { Transaction } from '../../../interfaces/ImportHistory';
import { SnackbarParameters } from '../../../interfaces/Snackbar';
import withSnackbar from '../../../shared/components/withSnackbar';
import { FILTERS } from '../../../shared/constants/ImportHistoryConstants';
import { customComponentColumns } from '../../../shared/helpers/importHistoryHelper';
import Toolbar from '../toolbar/Toolbar';
import DeleteProcessDialog from './DeleteProcessDialog';
import './ImportHistory.scss';
import { SovosCard, SovosLinearProgress, SovosMenuItem, SovosPaginationFooter, SovosTable } from 'mosaic-react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const noResultsFoundStyle = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginTop: 20,
	marginBottom: 20,
};

interface ImportHistoryProps {
	showSnackbar: (value: SnackbarParameters) => void;
}

const ImportHistory: React.FC<ImportHistoryProps> = (props: ImportHistoryProps) => {
	const { showSnackbar } = props;
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [selectedProcess, setSelectedProcess] = useState<Transaction>();
	const { getFile } = useContext(FileContext);
	const { setSelectedTransaction, filters, getImportData, importHistoryData, isLoading, handleFiltersChange } =
		useContext(BatchDeterminationContext);
	const navigate = useNavigate();

	const getActionMenuItems = (process: Transaction): JSX.Element[] => [
		<SovosMenuItem key={1} onClick={() => handleDownloadOriginal(process)}>
			<span>Download Original</span>
		</SovosMenuItem>,
		<SovosMenuItem
			key={2}
			style={process.isAudit || process.processingStatus === 'PROCESSING' ? { display: 'none' } : {}}
			onClick={() => handleDeleteProcess(process)}
		>
			<span>Remove Import</span>
		</SovosMenuItem>,
		<SovosMenuItem
			key={3}
			style={process.processingStatus === 'PROCESSING' ? { display: 'none' } : {}}
			onClick={() => handleDownloadExport(process)}
		>
			<span>Export</span>
		</SovosMenuItem>,
	];

	const handlePageChange = (newPage: number, newItemsPerPage: number): void => {
		handleFiltersChange(FILTERS.PAGE_STATE, { ...filters.pageState, page: newPage, perPage: newItemsPerPage });
	};

	const handleColumnSort = ({ id }: { id: number }): void => {
		const sortedColumn = filters.sortedColumn;
		const sort = {
			id,
			isAscending: sortedColumn.isAscending === null ? true : id === sortedColumn.id ? !sortedColumn.isAscending : true,
		};
		handleFiltersChange(FILTERS.SORTED_COLUMN, sort);
	};

	const handleDownloadOriginal = (process: Transaction): void => {
		getFile(process.fileGuid)
			.then(response => {
				if (response.status === 200) {
					showSnackbar({ message: 'Download in progress', variant: 'confirmation' });
					downloadFile(process.fileName, new Blob([response.data]));
				} else if (response.data.errors) {
					showSnackbar({ message: response.data.errors[0].message, variant: 'error' });
				} else {
					showSnackbar({ message: 'Unable to download Original File. Please try again later.', variant: 'error' });
				}
			})
			.catch(error => console.log(error));
	};

	const handleDownloadExport = (process: Transaction): void => {
		ImportHistoryApi.getProcess(process.processId)
			.then(response => {
				if (response.status === 201) {
					showSnackbar({ message: 'Your report is being generated.', variant: 'confirmation' });
				} else if (response.status === 206) {
					showSnackbar({
						message: 'Your report is still being generated. Please wait a few minutes and try again',
						variant: 'warning',
					});
				} else if (response.status === 200) {
					showSnackbar({
						message: 'Report generation was successful, download will start shortly',
						variant: 'confirmation',
					});
					downloadFile(getFileNameFromHeader(response.headers), new Blob([response.data]));
				} else if (response.data.errors) {
					showSnackbar({ message: response.data.errors[0].message, variant: 'error' });
				} else {
					showSnackbar({ message: 'Unable to Export. Please try again later.', variant: 'error' });
				}
			})
			.catch(error => console.log(error));
	};

	const handleDeleteProcess = (process: Transaction): void => {
		setSelectedProcess(process);
		setIsDeleteDialogOpen(true);
	};

	const closeDialog = (): void => {
		setIsDeleteDialogOpen(false);
	};

	const snackbarData = (value: SnackbarParameters): void => {
		if (value.open) {
			showSnackbar({ message: value.message, variant: value.variant });
			getImportData();
		}
	};

	return (
		<>
			<Toolbar />
			<SovosCard style={{ margin: 10 }}>
				{isLoading ? (
					<SovosLinearProgress />
				) : (
					<>
						{filters.pageState.totalEntries > 0 ? (
							<SovosTable
								columns={customComponentColumns}
								data={importHistoryData}
								onColumnSort={handleColumnSort}
								columnSortId={filters.sortedColumn.id}
								className='table-content'
								rowActions={getActionMenuItems}
								onRowClick={(row: Transaction) => {
									setSelectedTransaction(row);
									navigate('/import');
								}}
							/>
						) : (
							<div style={{ ...noResultsFoundStyle, flexDirection: 'column' }}>
								<h6 className='subtitle'>No results found</h6>
								<p style={{ fontSize: 14 }}>
									Try adjusting your filters or select a new date range to find what you&apos;re looking for.{' '}
								</p>
							</div>
						)}
						{filters.pageState.totalEntries > 0 && (
							<SovosPaginationFooter
								currentPage={filters.pageState.page}
								itemsPerPage={filters.pageState.perPage}
								totalItems={filters.pageState.totalEntries}
								onPageChange={handlePageChange}
							/>
						)}
						<DeleteProcessDialog
							isDialogOpen={isDeleteDialogOpen}
							closeDialog={closeDialog}
							process={selectedProcess!}
							snackbarData={snackbarData}
						/>
					</>
				)}
			</SovosCard>
		</>
	);
};

export default withSnackbar(ImportHistory);
